import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, Camera, ClipboardCheck, CheckCircle2, Plus, AlertCircle } from 'lucide-react';
import { dashboardApi } from '@/api/dashboard';
import type { DashboardStats } from '@/types';
import StatsCard from '@/components/StatsCard';
import DataTable from '@/components/DataTable';
import LoadingSpinner from '@/components/LoadingSpinner';
import EmptyState from '@/components/EmptyState';
import { formatDate, formatStatus, getStatusColor } from '@/utils/formatters';

export function Dashboard() {
  const navigate = useNavigate();

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: () => dashboardApi.getStats(),
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-surface-900">Dashboard</h1>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatsCard title="Total Clientes" value="" icon={Building2} color="#3B82F6" loading />
          <StatsCard title="Equipos CCTV Activos" value="" icon={Camera} color="#10B981" loading />
          <StatsCard title="Mantenimientos Pendientes" value="" icon={ClipboardCheck} color="#F59E0B" loading />
          <StatsCard title="Completados" value="" icon={CheckCircle2} color="#8B5CF6" loading />
        </div>
        <div className="rounded-lg border border-surface-200 bg-white p-6">
          <div className="h-64 animate-pulse rounded bg-surface-100" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-danger-200 bg-danger-50 p-8 text-center">
        <AlertCircle className="mb-2 h-8 w-8 text-danger-500" />
        <p className="text-sm font-medium text-danger-700">Error al cargar el dashboard</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-3 rounded-md bg-danger-100 px-3 py-1.5 text-sm font-medium text-danger-700 hover:bg-danger-200 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const stats: DashboardStats = response?.data?.data || {} as DashboardStats;

  const maintenanceColumns = [
    { key: 'scheduledDate', label: 'Fecha', sortable: true, render: (item: any) => formatDate(item.scheduledDate) },
    { key: 'clientName', label: 'Cliente', sortable: true, render: (item: any) => item.client?.name || item.clientName || '-' },
    { key: 'type', label: 'Tipo', render: (item: any) => (
      <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700">
        {item.type ? formatStatus(item.type) : '-'}
      </span>
    )},
    { key: 'status', label: 'Estado', sortable: true, render: (item: any) => (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(item.status)}`}>
        {formatStatus(item.status)}
      </span>
    )},
    { key: 'assignedTo', label: 'Técnico', render: (item: any) => item.assignedUser?.name || item.assignedTo || '-' },
  ];

  const totalMaintenance = stats.pendingMaintenance + stats.inProgressMaintenance + stats.completedMaintenance;
  const pendingPct = totalMaintenance > 0 ? Math.round((stats.pendingMaintenance / totalMaintenance) * 100) : 0;
  const inProgressPct = totalMaintenance > 0 ? Math.round((stats.inProgressMaintenance / totalMaintenance) * 100) : 0;
  const completedPct = totalMaintenance > 0 ? Math.round((stats.completedMaintenance / totalMaintenance) * 100) : 0;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-surface-900">Dashboard</h1>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Total Clientes"
          value={stats.totalClients}
          icon={Building2}
          color="#3B82F6"
        />
        <StatsCard
          title="Equipos CCTV Activos"
          value={stats.onlineCCTV}
          icon={Camera}
          color="#10B981"
        />
        <StatsCard
          title="Mantenimientos Pendientes"
          value={stats.pendingMaintenance}
          icon={ClipboardCheck}
          color="#F59E0B"
        />
        <StatsCard
          title="Completados"
          value={stats.completedMaintenance}
          icon={CheckCircle2}
          color="#8B5CF6"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-lg border border-surface-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-surface-900">Mantenimientos Recientes</h2>
          {stats.recentMaintenance && stats.recentMaintenance.length > 0 ? (
            <DataTable
              columns={maintenanceColumns}
              data={stats.recentMaintenance as any}
              onRowClick={(item: any) => navigate(`/maintenance/${item.id}`)}
            />
          ) : (
            <EmptyState title="No hay mantenimientos recientes" />
          )}
        </div>

        <div className="rounded-lg border border-surface-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-surface-900">Resumen por Estado</h2>
          {totalMaintenance > 0 ? (
            <div className="space-y-4">
              <div>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="text-surface-600">Pendientes</span>
                  <span className="font-medium text-surface-900">{stats.pendingMaintenance}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-surface-100">
                  <div className="h-full rounded-full bg-yellow-500 transition-all" style={{ width: `${pendingPct}%` }} />
                </div>
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="text-surface-600">En Progreso</span>
                  <span className="font-medium text-surface-900">{stats.inProgressMaintenance}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-surface-100">
                  <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${inProgressPct}%` }} />
                </div>
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="text-surface-600">Completados</span>
                  <span className="font-medium text-surface-900">{stats.completedMaintenance}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-surface-100">
                  <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${completedPct}%` }} />
                </div>
              </div>
            </div>
          ) : (
            <EmptyState title="Sin datos" description="No hay mantenimientos registrados" />
          )}

          <div className="mt-6 space-y-2">
            <h3 className="text-sm font-medium text-surface-700">Acciones Rápidas</h3>
            <div className="grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={() => navigate('/clients/new')}
                className="inline-flex items-center gap-2 rounded-md border border-surface-200 bg-white px-4 py-2 text-sm font-medium text-surface-700 shadow-sm hover:bg-surface-50 transition-colors"
              >
                <Plus className="h-4 w-4 text-primary-600" />
                Nuevo Cliente
              </button>
              <button
                type="button"
                onClick={() => navigate('/maintenance/new')}
                className="inline-flex items-center gap-2 rounded-md border border-surface-200 bg-white px-4 py-2 text-sm font-medium text-surface-700 shadow-sm hover:bg-surface-50 transition-colors"
              >
                <Plus className="h-4 w-4 text-primary-600" />
                Nuevo Mantenimiento
              </button>
              <button
                type="button"
                onClick={() => navigate('/cctv/new')}
                className="inline-flex items-center gap-2 rounded-md border border-surface-200 bg-white px-4 py-2 text-sm font-medium text-surface-700 shadow-sm hover:bg-surface-50 transition-colors"
              >
                <Plus className="h-4 w-4 text-primary-600" />
                Nuevo CCTV
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
